import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Site } from '@/api/endpoints';
import {
  Alert,
  Button,
  Card,
  Field,
  FormActions,
  Input,
  Select,
} from '@/components/ui';
import { useFormDraft } from '@/features/drafts';
import { enqueue } from '@/features/upload-queue';
import { CameraError, NORMAL_PRESET, capturePhoto } from '@/native';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/** 工地常见的采购类别。工人也能自己输，列表会累积出实际叫法。 */
const DEFAULT_CATEGORIES = ['五金', '水泥', '木材', '钢筋', '工具', '油漆', '电料', '其他'];

/** 被系统打断后要回到的地址。`compose=1` 让表单自己重新展开（CR-037） */
export const PURCHASE_COMPOSE_ROUTE = '/m/purchases?compose=1';

/**
 * 记一笔采购（CR-042 从采购页里抽出来）。
 *
 * **记到的永远是当前工地**，表单里没有工地选项。改之前这里有自己的工地下拉，
 * 而且是工地锁定的唯一豁免（CR-015）—— 客户这次推翻了它：
 * 「只能为当前工地买材料。如果还需要为其他工地买材料，可以在备注里填写」。
 */
export function PurchaseForm({
  site,
  userId,
  onDone,
  onCancel,
}: {
  site: Site;
  userId: number;
  onDone: () => void;
  /** 表单最下面的「取消」（按钮位置规矩：取消在表单里，不是页头按钮变出来的） */
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();

  /**
   * 表单落盘（CR-037）。金额、商家、备注都是工人对着小票一个一个敲进去的，
   * 拍完小票回来全空是最让人放弃这个 App 的一种体验。
   */
  const draft = useFormDraft(
    'purchase',
    userId,
    {
      amount: '',
      vendor: '',
      category: '',
      customCategory: '',
      note: '',
      purchasedAt: new Intl.DateTimeFormat('en-CA').format(new Date()),
    },
    PURCHASE_COMPOSE_ROUTE,
  );
  const { amount, vendor, category, customCategory, note, purchasedAt } = draft.fields;
  const shots = draft.shots;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'camera' | 'submit' | null>(null);

  const known = useQuery({
    queryKey: ['purchase-categories', site.id],
    queryFn: () => api.get<string[]>(`/sites/${site.id}/purchases/categories`),
  });

  const categories = [...new Set([...DEFAULT_CATEGORIES, ...(known.data ?? [])])];
  const effectiveCategory = category === '__custom__' ? customCategory.trim() : category;

  async function shootReceipt() {
    setError(null);
    setBusy('camera');
    try {
      const photo = await capturePhoto(NORMAL_PRESET, draft.captureSession('normal'));
      await draft.addShot({
        blob: photo.blob,
        mimeType: photo.mimeType,
        capturedAt: photo.capturedAt,
        isVideo: false,
      });
    } catch (err) {
      if (!(err instanceof CameraError && err.kind === 'CANCELLED')) {
        setError(t('hidden.cameraFailed'));
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // 界面填的是元，存的是分。用 Math.round 避免 19.99 * 100 = 1998.9999 这类浮点误差
    const cents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError(t('purchases.amountInvalid'));
      return;
    }

    setBusy('submit');
    try {
      await enqueue({
        kind: 'PURCHASE',
        userId,
        siteId: site.id,
        payload: {
          purchasedAt: new Date(`${purchasedAt}T12:00:00`).toISOString(),
          vendor: vendor.trim() || undefined,
          category: effectiveCategory || undefined,
          amountCents: cents,
          note: note.trim() || undefined,
        },
        files: shots.map((shot) => ({ blob: shot.blob, mimeType: shot.mimeType })),
      });

      // 进了队列就不再是草稿了。列表里那条「上传中」由队列快照渲染
      await draft.clear();
      onDone();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mb-5 p-5">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert>{error}</Alert>}

        {draft.restored && (
          <p className="flex items-center justify-between gap-3 rounded-md bg-surface-2 px-3 py-2 text-caption text-ink-2">
            <span>{t('drafts.restored')}</span>
            <button type="button" onClick={() => void draft.clear()} className="shrink-0 underline">
              {t('drafts.discard')}
            </button>
          </p>
        )}

        {/* 金额放最上面且做大：工人拿着小票，先输的就是这个数 */}
        <Field label={`${t('purchases.amount')} (${site.currency})`} htmlFor="pc-amount" required>
          <Input
            id="pc-amount"
            value={amount}
            onChange={(e) => draft.setField('amount', e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
            className="text-figure font-semibold tabular-nums"
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t('purchases.date')} htmlFor="pc-date" required>
            <Input
              id="pc-date"
              type="date"
              value={purchasedAt}
              onChange={(e) => draft.setField('purchasedAt', e.target.value)}
              required
            />
          </Field>
          <Field label={t('purchases.category')} htmlFor="pc-cat">
            <Select
              id="pc-cat"
              value={category}
              onChange={(e) => draft.setField('category', e.target.value)}
            >
              <option value="">—</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value="__custom__">{t('hidden.customCategory')}</option>
            </Select>
          </Field>
        </div>

        {category === '__custom__' && (
          <Field label={t('hidden.customCategory')} htmlFor="pc-custom">
            <Input
              id="pc-custom"
              value={customCategory}
              onChange={(e) => draft.setField('customCategory', e.target.value)}
              maxLength={64}
            />
          </Field>
        )}

        <Field label={t('purchases.vendor')} htmlFor="pc-vendor">
          <Input
            id="pc-vendor"
            value={vendor}
            onChange={(e) => draft.setField('vendor', e.target.value)}
            maxLength={120}
          />
        </Field>

        {/* 为别的工地买的料写在这里（CR-042：采购不再跨工地） */}
        <Field label={t('purchases.note')} htmlFor="pc-note" hint={t('purchases.noteOtherSiteHint')}>
          <Input
            id="pc-note"
            value={note}
            onChange={(e) => draft.setField('note', e.target.value)}
            maxLength={1000}
          />
        </Field>

        <div>
          <p className="mb-2 text-body font-medium text-ink-2">
            {t('purchases.receipt')}
            {shots.length > 0 && (
              <span className="ml-2 font-normal text-ink-3">
                {t('hidden.photoCount', { count: shots.length })}
              </span>
            )}
          </p>

          {shots.length > 0 && (
            <ul className="mb-3 grid grid-cols-3 gap-2">
              {shots.map((shot) => (
                <li key={shot.id} className="relative">
                  <img
                    src={shot.url}
                    alt=""
                    className="aspect-square w-full rounded-md border border-rule object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => void draft.removeShot(shot.id)}
                    aria-label={t('app.delete')}
                    className="absolute right-1 top-1 rounded-md bg-black/60 px-1.5 text-caption leading-6 text-white"
                  >
                    {t('app.delete')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Button
            type="button"
            variant="secondary"
            block
            loading={busy === 'camera'}
            onClick={() => void shootReceipt()}
          >
            {shots.length === 0 ? t('purchases.shootReceipt') : t('hidden.takeAnother')}
          </Button>
          <p className="mt-2 text-caption leading-relaxed text-ink-3">{t('purchases.receiptHint')}</p>
        </div>

        <FormActions
          className="mt-2"
          submitLabel={t('purchases.submit')}
          cancelLabel={t('app.cancel')}
          onCancel={onCancel}
          loading={busy === 'submit'}
        />
      </form>
    </Card>
  );
}
